export default function DealingWithProblemTenants() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-user-settings-line"></i>
            <span>Problem Resolution</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Dealing with Problem Tenants During the Sale Process
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Problem tenants can derail your sale or significantly reduce your property value. Learn how to address issues strategically before listing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/e865d9fbe7a65ef472c196683b181a25.jpg"
                alt="Managing problem tenants and resolving conflicts during apartment building sale"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Common Problem Tenant Scenarios</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              Problem tenants can significantly impact your property's marketability and value. Buyers conduct thorough due diligence on tenant quality, payment history, and lease compliance. Addressing these issues before listing is crucial for maximizing your sale price.
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-money-dollar-circle-line text-red-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-red-900 mb-2">Chronic Late Payers</h4>
                    <p className="text-red-800 text-sm mb-3">
                      Tenants who consistently pay rent late or require multiple reminders create operational headaches and reduce property value by 5-10%.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Solutions:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Implement strict late fee enforcement</li>
                        <li>• Require automatic payment enrollment</li>
                        <li>• Offer cash-for-keys to problem tenants</li>
                        <li>• Begin eviction process if necessary</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line text-amber-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-2">Lease Violators</h4>
                    <p className="text-amber-800 text-sm mb-3">
                      Tenants who violate lease terms (unauthorized occupants, pets, subletting, noise complaints) create liability and reduce buyer confidence.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Solutions:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Issue formal cure or quit notices</li>
                        <li>• Document all violations thoroughly</li>
                        <li>• Offer lease amendments to legitimize situations</li>
                        <li>• Pursue eviction for serious violations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-tools-line text-blue-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Maintenance Complainers</h4>
                    <p className="text-blue-800 text-sm mb-3">
                      Tenants with extensive maintenance request histories or pending repair issues can raise red flags during buyer due diligence.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Solutions:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Complete all legitimate repair requests immediately</li>
                        <li>• Document unreasonable or frivolous requests</li>
                        <li>• Conduct pre-sale unit inspections</li>
                        <li>• Address deferred maintenance proactively</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-5 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <i className="ri-user-unfollow-line text-purple-600 text-2xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-purple-900 mb-2">Hostile or Litigious Tenants</h4>
                    <p className="text-purple-800 text-sm mb-3">
                      Tenants with history of complaints, lawsuits, or hostile behavior toward management can significantly reduce property value.
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Solutions:</p>
                      <ul className="text-xs text-slate-600 space-y-1">
                        <li>• Maintain professional communication only</li>
                        <li>• Document all interactions meticulously</li>
                        <li>• Offer generous buyout packages</li>
                        <li>• Disclose history fully to buyer</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Strategic Approaches to Problem Tenants</h3>

            <div className="space-y-4">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-coin-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Cash-for-Keys Buyouts</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Offer problem tenants $1,000-$5,000 to voluntarily vacate before listing. This is often cheaper and faster than eviction, and removes the problem entirely.
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-time-line text-green-600"></i>
                        <span>2-4 weeks vs 3-6 months</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-money-dollar-circle-line text-green-600"></i>
                        <span>$2K-$5K cost</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-edit-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Lease Amendments and Fresh Starts</h4>
                    <p className="text-slate-600 text-sm">
                      For salvageable situations, offer lease amendments that address violations while giving tenants a clean slate. This can convert problem tenants into acceptable ones for buyers.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Full Disclosure to Buyers</h4>
                    <p className="text-slate-600 text-sm">
                      Be completely transparent about problem tenants in your disclosure documents. Buyers will discover issues during due diligence anyway, and honesty builds trust.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-scales-3-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Eviction as Last Resort</h4>
                    <p className="text-slate-600 text-sm">
                      If other options fail, begin eviction proceedings well before listing. A unit in eviction is better than a problem tenant, as buyers can see you're addressing the issue.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Impact of Problem Tenants on Property Value</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Issue Type</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Severity</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Value Impact</th>
                  <th className="text-left py-4 px-4 font-semibold text-slate-900">Recommended Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Occasional late payment (1-2x/year)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-checkbox-circle-line"></i>
                      Low
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">Minimal to none</td>
                  <td className="py-4 px-4 text-slate-600">Document and disclose</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Chronic late payment (monthly)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-semibold">
                      <i className="ri-error-warning-line"></i>
                      Medium
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-5% to -10%</td>
                  <td className="py-4 px-4 text-slate-600">Cash-for-keys or strict enforcement</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Current rent arrears (&gt;30 days)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      High
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-10% to -15%</td>
                  <td className="py-4 px-4 text-slate-600">Immediate eviction or buyout</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Minor lease violations</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                      <i className="ri-checkbox-circle-line"></i>
                      Low
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-2% to -5%</td>
                  <td className="py-4 px-4 text-slate-600">Issue cure notice and resolve</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Serious violations (unauthorized occupants)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      High
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-8% to -12%</td>
                  <td className="py-4 px-4 text-slate-600">Eviction or lease amendment</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-4 px-4 text-slate-700">Pending litigation with tenant</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      Critical
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-15% to -25%</td>
                  <td className="py-4 px-4 text-slate-600">Settle before listing or significant discount</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-700">Multiple problem tenants (&gt;20% of units)</td>
                  <td className="py-4 px-4">
                    <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                      <i className="ri-alert-line"></i>
                      Critical
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-600">-20% to -35%</td>
                  <td className="py-4 px-4 text-slate-600">Delay sale until issues resolved</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-sm text-slate-600 mt-6 italic">
            * Value impacts are estimates based on typical buyer reactions and may vary by market and property type
          </p>
        </div>
      </div>
    </section>
  );
}
